import capitalize from "lodash/capitalize";
import styled from "styled-components";

import AnchoredPopover from "~/components/shared/Popover/anchored";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { getDateString } from "~/lib/date";
import Emojify from "~/components/shared/Emojify";
import ExecutionDrawerButton from "~/components/analytics/execution/Row/ExecutionDrawerButton";
import Icon from "~/components/shared/Icon";

export type ActivityProps = {
  id: string | number;
  name: string;
  duration: number;
  result: string;
  date: string;
  url: string;
  turboFrameSrc?: string;
  humanDuration: string;
  createdAt: string;
  runId?: string;
  branch: string | null | undefined;
  buildNumber: string | null | undefined;
};

type Props = {
  activity: ActivityProps;
  height: number;
};

const RESULT_COLOUR = {
  passed: "green",
  failed: "red",
};

export const StyledAnchoredPopover = styled.div`
  width: 100%;
  height: 100%;
  padding: 0px 1px;
  max-width: 12px;

  > a {
    height: 100%;
  }
`;

const StyledBar = ({ barColour }: { barColour: string }) => (
  <span
    data-testid="styled-bar"
    className={`transition-height block h-full w-full rounded-t-[2px] duration-200 ease-in ${barColour}`}
  />
);

const Bar = ({ activity, height }: Props) => {
  const barHeight = Math.round(height * 100.0);

  const colour = RESULT_COLOUR[activity.result];
  const barColour = colour ? `ta-bg-${colour} hover-ta-bg-${colour}` : "bg-gray";

  const linkLabel = `Go to ${activity.name}. ${capitalize(
    activity.result,
  )} on ${getDateString(activity.createdAt)} — ${activity.humanDuration}`;

  // User is viewing activity bar on test#show
  if (activity.turboFrameSrc) {
    return (
      <ExecutionDrawerButton
        label={linkLabel}
        url={activity.turboFrameSrc}
        classes="no-title-tooltip w-full relative block pt-[1px]"
        style={{ height: `${barHeight}%` }}
      >
        <StyledBar barColour={barColour} />
      </ExecutionDrawerButton>
    );
  }

  // User is viewing activity bar on suite#show
  return (
    <StyledAnchoredPopover
      as={AnchoredPopover}
      className="flex"
      data-testid="bar"
      style={{
        height: `${barHeight}%`,
      }}
      width={300}
    >
      <a
        aria-label={linkLabel}
        href={activity.url}
        className="no-title-tooltip relative block h-full w-full"
      >
        <StyledBar barColour={barColour} />
      </a>

      <div className="flex flex-col gap-1 px-2 py-1">
        <span className="block break-words font-medium">
          <Emojify text={activity.name} />
        </span>

        {activity.buildNumber && (
          <div className="dark-gray small">Build #{activity.buildNumber}</div>
        )}

        {activity.branch && (
          <div className="dark-gray small">
            <Icon icon="custom/outline/14x/branch" style={{ height: 14, width: 14 }} />{" "}
            {activity.branch}
          </div>
        )}

        <span className="small dark-gray block">
          {capitalize(activity.result)}{" "}
          <FriendlyTime value={activity.createdAt} clickable={false} /> - {activity.humanDuration}
        </span>
      </div>
    </StyledAnchoredPopover>
  );
};

export default Bar;
